import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@mui/material";
import { useTranslation } from "react-i18next";

const APPLE_SCRIPT_SRC = "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";

let appleScriptPromise = null;

function loadAppleScript() {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Apple Sign-In requires a browser environment."));
  }
  if (window.AppleID?.auth) {
    return Promise.resolve(window.AppleID);
  }
  if (!appleScriptPromise) {
    appleScriptPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${APPLE_SCRIPT_SRC}"]`);
      if (existing) {
        existing.addEventListener("load", () => resolve(window.AppleID));
        existing.addEventListener("error", () => reject(new Error("Apple Sign-In SDK failed to load.")));
        return;
      }
      const script = document.createElement("script");
      script.src = APPLE_SCRIPT_SRC;
      script.async = true;
      script.onload = () => resolve(window.AppleID);
      script.onerror = () => reject(new Error("Apple Sign-In SDK failed to load."));
      document.body.appendChild(script);
    });
  }
  return appleScriptPromise;
}

export default function AppleSignInButton({
  onSuccess,
  onError,
  disabled = false,
  label,
  clientId = "",
  redirectURI = "",
}) {
  const { t } = useTranslation();
  const [ready, setReady] = useState(Boolean(typeof window !== "undefined" && window.AppleID?.auth));
  const [loading, setLoading] = useState(false);
  const resolvedClientId = useMemo(() => String(clientId || "").trim(), [clientId]);
  const resolvedRedirectURI = useMemo(
    () =>
      String(
        redirectURI ||
          (typeof window !== "undefined" ? `${window.location.origin}/auth/apple/callback` : "")
      ).trim(),
    [redirectURI]
  );

  useEffect(() => {
    if (!resolvedClientId) {
      setReady(false);
      return undefined;
    }

    loadAppleScript()
      .then(() => setReady(true))
      .catch((error) => {
        if (onError) onError(error);
      });
    return undefined;
  }, [onError, resolvedClientId]);

  const handlePress = useCallback(async () => {
    if (!resolvedClientId) {
      return;
    }

    setLoading(true);
    try {
      await loadAppleScript();
      window.AppleID.auth.init({
        clientId: resolvedClientId,
        scope: "name email",
        redirectURI: resolvedRedirectURI,
        usePopup: true,
      });
      const response = await window.AppleID.auth.signIn();
      const authorization = response?.authorization || {};
      const user = response?.user || null;
      if (!authorization.id_token) {
        throw new Error("Apple Sign-In did not return an identity token.");
      }
      await onSuccess?.({
        idToken: authorization.id_token,
        authorizationCode: authorization.code || null,
        user,
      });
    } catch (error) {
      if (String(error?.error || "").toLowerCase() === "popup_closed_by_user") {
        setLoading(false);
        return;
      }
      onError?.(error);
    } finally {
      setLoading(false);
    }
  }, [onError, onSuccess, resolvedClientId, resolvedRedirectURI]);

  if (!resolvedClientId) {
    return null;
  }

  return (
    <Button
      variant="outlined"
      fullWidth
      disabled={disabled || loading || !ready}
      onClick={handlePress}
      sx={{
        py: 1.35,
        borderRadius: 999,
        borderColor: "divider",
        color: "text.primary",
        fontWeight: 700,
        textTransform: "none",
      }}
    >
      {loading ? t("connecting") : label || t("login_with_apple")}
    </Button>
  );
}
